import { useState } from "react";
import type { FormEvent } from "react";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";

import type { RoleOption } from "@/modules/roles";
import type { User } from "@/modules/users";
import styles from "./edit-user-dialog.module.scss";

export const EDIT_USER_TITLE = "Editar Usuário";
export const IDENTITY_TITLE = "Informações do Usuário";
export const ROLE_REQUIRED_MESSAGE = "Campo obrigatório";
export const ROLE_PLACEHOLDER = "Selecione";
export const ROLE_SEARCH_PLACEHOLDER = "Buscar perfil";
export const ROLES_ERROR_MESSAGE = "Não foi possível carregar os perfis.";
export const ROLES_EMPTY_MESSAGE = "Nenhum perfil cadastrado.";
export const ROLES_EMPTY_FILTER_MESSAGE = "Nenhum perfil encontrado.";

const EMPTY_FIELD = "—";

/** Liga o botão Salvar, que vive no rodapé do Dialog, ao formulário do corpo. */
const FORM_ID = "edit-user-form";

export interface EditUserValues {
  papelId: string;
}

interface EditUserDialogProps {
  user: User;
  roleOptions: RoleOption[];
  isLoadingRoles: boolean;
  hasRolesError: boolean;
  /**
   * Persiste o perfil. Cabe ao chamador comunicar o resultado e fechar o modal
   * no sucesso — em caso de falha o modal segue aberto com a seleção feita.
   * A promessa não deve ser rejeitada.
   */
  onSubmit: (values: EditUserValues) => Promise<void>;
  onCancel: () => void;
}

function getInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";

  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";

  return `${first}${last}`.toUpperCase();
}

export function EditUserDialog({
  user,
  roleOptions,
  isLoadingRoles,
  hasRolesError,
  onSubmit,
  onCancel,
}: EditUserDialogProps) {
  const [papelId, setPapelId] = useState(user.papelId);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleRole(value: string) {
    setPapelId(value);
    if (value) setError(null);
  }

  async function copyEmail() {
    if (!user.email) return;

    try {
      await navigator.clipboard.writeText(user.email);
    } catch {
      return;
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // RN03: a identidade vem do diretório corporativo e nunca é enviada.
    if (!papelId) {
      setError(ROLE_REQUIRED_MESSAGE);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit({ papelId });
    } finally {
      setIsSubmitting(false);
    }
  }

  // O rodapé é uma faixa própria do Dialog, portanto fica fora do <form>:
  // o atributo `form` mantém Salvar como botão de envio do formulário.
  const footer = (
    <div className={styles.actions}>
      <Button
        type="button"
        className={styles.cancelButton}
        label="Cancelar"
        severity="secondary"
        text
        onClick={onCancel}
        disabled={isSubmitting}
        data-testid="edit-user-cancel"
      />
      <Button
        type="submit"
        form={FORM_ID}
        className={styles.saveButton}
        label="Salvar"
        loading={isSubmitting}
        data-testid="edit-user-save"
      />
    </div>
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      contentClassName={styles.content}
      header={EDIT_USER_TITLE}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      style={{ width: "495px" }}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="edit-user-dialog"
    >
      <form id={FORM_ID} className={styles.form} onSubmit={handleSubmit} noValidate>
        <section className={styles.identity} data-testid="edit-user-identity">
          <h2 className={styles.identityTitle}>{IDENTITY_TITLE}</h2>
          <div className={styles.identityDivider} />

          <div className={styles.identityRow}>
            {user.fotoUrl ? (
              <Avatar
                className={styles.avatar}
                image={user.fotoUrl}
                imageAlt={`Foto de ${user.nome}`}
                shape="circle"
              />
            ) : (
              <Avatar
                className={styles.avatar}
                label={getInitials(user.nome)}
                shape="circle"
                aria-label={`Sem foto de ${user.nome}`}
              />
            )}

            <div className={styles.userInfo}>
              <p className={styles.userName} data-testid="edit-user-name">
                {user.nome || EMPTY_FIELD}
              </p>

              <ul className={styles.contacts}>
                <li className={`${styles.contact} ${styles.contactEmail}`}>
                  <span className={styles.contactMain}>
                    <i className={`pi pi-envelope ${styles.contactIcon}`} aria-hidden />
                    <span className={styles.contactText} data-testid="edit-user-email">
                      {user.email || EMPTY_FIELD}
                    </span>
                  </span>
                  {user.email ? (
                    <button
                      type="button"
                      className={styles.copyButton}
                      onClick={copyEmail}
                      aria-label={`Copiar e-mail de ${user.nome}`}
                      data-testid="edit-user-copy-email"
                    >
                      <i className="pi pi-copy" aria-hidden />
                    </button>
                  ) : null}
                </li>
                <li className={styles.contact}>
                  <i className={`pi pi-phone ${styles.contactIcon}`} aria-hidden />
                  <span className={styles.contactText} data-testid="edit-user-phone">
                    {user.celular || EMPTY_FIELD}
                  </span>
                </li>
                <li className={styles.contact}>
                  <i className={`pi pi-users ${styles.contactIcon}`} aria-hidden />
                  <span className={styles.contactText} data-testid="edit-user-team">
                    {user.time || EMPTY_FIELD}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-user-role">
            Perfil
          </label>
          <Dropdown
            inputId="edit-user-role"
            className={`${styles.dropdown} ${error ? styles.dropdownInvalid : ""}`}
            panelClassName={styles.dropdownPanel}
            value={papelId}
            options={roleOptions}
            optionLabel="label"
            optionValue="value"
            placeholder={ROLE_PLACEHOLDER}
            filter
            filterBy="label"
            filterDelay={0}
            filterPlaceholder={ROLE_SEARCH_PLACEHOLDER}
            emptyFilterMessage={ROLES_EMPTY_FILTER_MESSAGE}
            emptyMessage={hasRolesError ? ROLES_ERROR_MESSAGE : ROLES_EMPTY_MESSAGE}
            appendTo="self"
            onChange={(event) => handleRole((event.value as string | null) ?? "")}
            disabled={isSubmitting || isLoadingRoles}
            required
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "edit-user-role-error" : undefined}
            data-testid="edit-user-role"
          />
          {error ? (
            <span
              id="edit-user-role-error"
              className={styles.fieldError}
              role="alert"
              data-testid="edit-user-role-error"
            >
              {error}
            </span>
          ) : null}
        </div>
      </form>
    </Dialog>
  );
}
